import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle2, 
  ChevronRight,
  Code2,
  Database,
  Lightbulb,
  Users
} from 'lucide-react';
import { serviceAPI } from '../services/api';

const ServiceDetail = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchService();
  }, [slug]);

  const fetchService = async () => {
    try {
      const response = await serviceAPI.getBySlug(slug);
      setService(response.data.service);
    } catch (error) {
      console.error('Error fetching service:', error);
      // Use default data if API fails
      setService(getDefaultService(slug));
    } finally {
      setLoading(false);
    }
  };

  const getDefaultService = (slug) => {
    const services = {
      'it-development': {
        name: 'IT Development & Maintenance',
        fullDescription: 'Transform your business ideas into powerful, scalable software solutions. Our development teams combine technical excellence with business acumen to deliver applications that drive real value.',
        features: [
          { title: 'Web Application Development', description: 'Build modern, responsive, and secure web applications that engage users and drive business outcomes.' },
          { title: 'Custom Software Development', description: 'Off-the-shelf software rarely fits perfectly. We build custom solutions tailored to your unique business processes.' },
          { title: 'Application Support & Maintenance', description: 'Keep your applications running at peak performance with our comprehensive support services.' },
          { title: 'Legacy Application Modernization', description: 'Don\'t let outdated technology hold you back. We help you modernize legacy systems.' }
        ],
        technologies: [
          { name: 'React', description: 'Modern frontend framework' },
          { name: 'Node.js', description: 'Scalable backend runtime' },
          { name: 'Python', description: 'Versatile programming language' },
          { name: 'Java', description: 'Enterprise-grade development' }
        ]
      },
      'erp-solutions': {
        name: 'ERP Development & Maintenance',
        fullDescription: 'Unlock the full potential of your enterprise resource planning systems. Our certified consultants bring deep platform expertise to optimize, customize, and support your ERP environment.',
        features: [
          { title: 'Oracle Solutions', description: 'Implementation, customization, and support for Oracle ERP Cloud and E-Business Suite.' },
          { title: 'SAP Solutions', description: 'End-to-end SAP consulting and development for S/4HANA and ECC.' },
          { title: 'Salesforce Solutions', description: 'CRM optimization and custom development on the Salesforce platform.' },
          { title: 'Microsoft Dynamics', description: 'Business applications tailored to your needs with Dynamics 365.' }
        ],
        technologies: [
          { name: 'Oracle', description: 'Enterprise ERP platform' },
          { name: 'SAP', description: 'Leading ERP solution' },
          { name: 'Salesforce', description: 'Cloud-based CRM' },
          { name: 'Dynamics 365', description: 'Microsoft business apps' }
        ]
      },
      'it-consulting': {
        name: 'IT Consulting',
        fullDescription: 'Navigate the complexities of technology decision-making with confidence. Our consultants bring decades of combined experience to help you build a technology foundation that supports your business goals.',
        features: [
          { title: 'Digital Transformation Strategy', description: 'Roadmaps for enterprise modernization and digital adoption.' },
          { title: 'ERP Advisory', description: 'Platform selection, implementation planning, and optimization.' },
          { title: 'Cloud Strategy', description: 'Migration planning and cloud-native architecture design.' },
          { title: 'Enterprise IT Architecture', description: 'Scalable, secure technology foundations for growth.' }
        ],
        technologies: [
          { name: 'AWS', description: 'Cloud infrastructure' },
          { name: 'Azure', description: 'Microsoft cloud platform' },
          { name: 'GCP', description: 'Google Cloud Platform' },
          { name: 'Kubernetes', description: 'Container orchestration' }
        ]
      },
      'manpower-consulting': {
        name: 'Manpower Consulting',
        fullDescription: 'Bridge your talent gaps with our extensive network of technology professionals. From short-term augmentation to permanent placements, we connect you with the right expertise at the right time.',
        features: [
          { title: 'IT Staff Augmentation', description: 'Scale your team with pre-vetted professionals on demand.' },
          { title: 'Dedicated Development Teams', description: 'Full teams focused exclusively on your projects.' },
          { title: 'ERP Specialists', description: 'Platform-certified consultants for contract and permanent roles.' },
          { title: 'Technical Recruitment', description: 'Find the perfect fit for your permanent positions.' }
        ],
        technologies: [
          { name: 'Full Stack', description: 'End-to-end developers' },
          { name: 'DevOps', description: 'Infrastructure specialists' },
          { name: 'Data Science', description: 'Analytics experts' },
          { name: 'QA Engineers', description: 'Quality assurance professionals' }
        ]
      }
    };
    return services[slug] || services['it-development'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 gradient-hero">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-2 text-white/70 mb-4">
              <Link to="/services" className="hover:text-white">Services</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">{service?.name}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {service?.name}
            </h1>
            <p className="text-lg text-white/80 max-w-2xl">
              {service?.fullDescription}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              What We Offer
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {service?.features?.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4 p-6 bg-slate-50 rounded-xl"
              >
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center text-accent flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-primary mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="section bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Technologies We Work With
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {service?.technologies?.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm text-center"
              >
                <h3 className="text-lg font-bold text-primary mb-1">{tech.name}</h3>
                <p className="text-slate-500 text-sm">{tech.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Let's discuss how our {service?.name} services can help your business.
            </p>
            <Link to="/contact" className="btn bg-white text-accent hover:bg-slate-100 btn-lg">
              Request a Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
